import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Reveal, SectionEyebrow, SectionTitle, SectionBody, Container } from './Primitives';
import { useLanguage } from '@/context/LanguageContext';
import { SITE } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    organization: '',
    facility_type: '',
    email: '',
    phone: '',
    message: '',
    website: '', // honeypot — must remain empty
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const mountTimeRef = React.useRef(Date.now());

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateSelect = (v) => setForm((f) => ({ ...f, facility_type: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        organization: form.organization.trim() || null,
        facility_type: form.facility_type || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
        website: form.website, // honeypot
        elapsed_ms: Date.now() - mountTimeRef.current,
      };
      const res = await axios.post(`${API}/contact`, payload);
      setSuccess(true);
      // Auto-clear success indicator after 6s
      setTimeout(() => setSuccess(false), 6000);
      toast.success(t.contact.form.successTitle, {
        description: res.data?.message || t.contact.form.successDesc,
      });
      setForm({
        name: '',
        organization: '',
        facility_type: '',
        email: '',
        phone: '',
        message: '',
        website: '',
      });
      // reset timer for next submission
      mountTimeRef.current = Date.now();
    } catch (err) {
      toast.error(t.contact.form.errorTitle, {
        description:
          err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          t.contact.form.errorDesc,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const opt = t.contact.form.facilityOptions;

  return (
    <section
      id="contact"
      data-testid={SITE.sectionContact}
      className="relative py-20 lg:py-32 bg-white"
    >
      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionEyebrow>{t.contact.eyebrow}</SectionEyebrow>
            </Reveal>
            <Reveal delay={0.05}>
              <SectionTitle className="mt-4">{t.contact.title}</SectionTitle>
            </Reveal>
            <Reveal delay={0.1}>
              <SectionBody className="mt-6">{t.contact.body}</SectionBody>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="mt-10 space-y-4">
                <a
                  href={`mailto:${t.contact.email}`}
                  className="flex items-center gap-4 p-5 rounded-2xl bg-[#F7F8F5] border border-[rgba(0,166,81,0.1)] hover:border-[rgba(0,166,81,0.4)] transition-colors group"
                >
                  <div className="w-11 h-11 rounded-xl bg-[#00A651]/10 flex items-center justify-center">
                    <Mail size={20} className="text-[#00A651]" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-[#5C5C5C] font-semibold">
                      {t.contact.form.email}
                    </div>
                    <div className="font-['Outfit'] text-lg font-medium text-[#0B0F0C] group-hover:text-[#00A651] transition-colors">
                      {t.contact.email}
                    </div>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-5 rounded-2xl bg-[#F7F8F5] border border-[rgba(0,166,81,0.1)]">
                  <div className="w-11 h-11 rounded-xl bg-[#00A651]/10 flex items-center justify-center">
                    <Clock size={20} className="text-[#00A651]" />
                  </div>
                  <div className="text-sm text-[#0B0F0C]">{t.contact.hours}</div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={onSubmit}
                data-testid={SITE.contactForm}
                className="relative bg-[#0B0F0C] text-white rounded-3xl p-7 sm:p-9 lg:p-10 border border-[rgba(34,197,94,0.15)] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.45)] overflow-hidden"
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -top-32 -right-20 w-[400px] h-[400px] rounded-full blur-3xl"
                  style={{
                    background:
                      'radial-gradient(circle, rgba(0,166,81,0.18) 0%, transparent 60%)',
                  }}
                />

                <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Honeypot field — hidden from real users, only bots fill it */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '-10000px',
                      top: 'auto',
                      width: '1px',
                      height: '1px',
                      overflow: 'hidden',
                    }}
                  >
                    <label htmlFor="c-website">Website</label>
                    <input
                      id="c-website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={update('website')}
                    />
                  </div>

                  <Field label={t.contact.form.name} htmlFor="c-name" required>
                    <input
                      id="c-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={update('name')}
                      placeholder={t.contact.form.placeholderName}
                      data-testid={SITE.contactName}
                      className={inputCls}
                    />
                  </Field>

                  <Field label={t.contact.form.organization} htmlFor="c-org">
                    <input
                      id="c-org"
                      type="text"
                      value={form.organization}
                      onChange={update('organization')}
                      placeholder={t.contact.form.placeholderOrg}
                      data-testid={SITE.contactOrganization}
                      className={inputCls}
                    />
                  </Field>

                  <Field label={t.contact.form.facilityType} htmlFor="c-type" full>
                    <Select value={form.facility_type} onValueChange={updateSelect}>
                      <SelectTrigger
                        id="c-type"
                        data-testid={SITE.contactFacilityType}
                        className="w-full h-12 bg-white/5 border-white/10 text-white rounded-xl px-4 hover:border-[#22C55E]/50 focus:ring-[#22C55E]"
                      >
                        <SelectValue placeholder={t.contact.form.placeholderType} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0B0F0C] text-white border-white/10">
                        <SelectItem value="ri">{opt.ri}</SelectItem>
                        <SelectItem value="rpa">{opt.rpa}</SelectItem>
                        <SelectItem value="chsld">{opt.chsld}</SelectItem>
                        <SelectItem value="orgsoc">{opt.orgsoc}</SelectItem>
                        <SelectItem value="other">{opt.other}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label={t.contact.form.email} htmlFor="c-email" required>
                    <input
                      id="c-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update('email')}
                      placeholder={t.contact.form.placeholderEmail}
                      data-testid={SITE.contactEmail}
                      className={inputCls}
                    />
                  </Field>

                  <Field label={t.contact.form.phone} htmlFor="c-phone">
                    <input
                      id="c-phone"
                      type="tel"
                      value={form.phone}
                      onChange={update('phone')}
                      placeholder={t.contact.form.placeholderPhone}
                      data-testid={SITE.contactPhone}
                      className={inputCls}
                    />
                  </Field>

                  <Field label={t.contact.form.message} htmlFor="c-msg" required full>
                    <textarea
                      id="c-msg"
                      required
                      rows={5}
                      value={form.message}
                      onChange={update('message')}
                      placeholder={t.contact.form.placeholderMessage}
                      data-testid={SITE.contactMessage}
                      className={`${inputCls} resize-y min-h-[120px] py-3`}
                    />
                  </Field>
                </div>

                <div className="relative mt-7 flex items-center justify-between gap-4 flex-wrap">
                  {success ? (
                    <div
                      data-testid={SITE.contactSuccess}
                      className="flex items-center gap-2 text-[#22C55E] text-sm font-medium"
                    >
                      <CheckCircle2 size={18} /> {t.contact.form.successTitle}
                    </div>
                  ) : (
                    <span className="text-xs text-white/45">
                      {t.contact.email}
                    </span>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid={SITE.contactSubmit}
                    className="inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#22C55E] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3.5 px-7 rounded-xl transition-all duration-300 hover:-translate-y-0.5 shadow-[0_12px_30px_-8px_rgba(34,197,94,0.6)] group"
                  >
                    {submitting ? t.contact.form.submitting : t.contact.form.submit}
                    {!submitting && (
                      <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                    )}
                  </button>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </Container>
    </section>
  );
};

const inputCls =
  'w-full h-12 bg-white/5 border border-white/10 text-white placeholder:text-white/35 rounded-xl px-4 outline-none focus:border-[#22C55E]/60 focus:ring-2 focus:ring-[#22C55E]/20 transition-all';

const Field = ({ label, htmlFor, required, full, children }) => (
  <div className={full ? 'sm:col-span-2' : ''}>
    <label htmlFor={htmlFor} className="block text-xs font-semibold uppercase tracking-[0.16em] text-white/65 mb-2">
      {label}{required ? <span className="text-[#22C55E]"> *</span> : null}
    </label>
    {children}
  </div>
);

export default Contact;
